import React, { useContext } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import styles from "./CommunicationTimelineEntry.module.css";
import { RootStoreContext } from "src/stores/RootStore";
import { COMMUNICATION_TIMELINE_ENTRY, ICommunicationEntry } from "src/stores/CommunicationStore";
import { CommunicationPublishButtons } from "./buttons/CommunicationPublishButtons";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";
import { CommunicationNotifyButtons } from "./buttons/CommunicationNotifyButtons";
import { CommunicationCompletionButton } from "./buttons/CommunicationCompletionButton";

interface CommunicationTimelineEntryProps {
    entry: ICommunicationEntry;
}

const CommunicationTimelineEntryBase = (props: CommunicationTimelineEntryProps) => {
    const { t } = useTranslation();
    const { assemblyStore, communicationStore } = useContext(RootStoreContext);
    const { state, isActive, statusChangeEntry, publishChangeEntry } = props.entry;
    const assemblyState = assemblyStore.currentAssembly?.state;

    const isNotificationButtonVisible =
        state === COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED ||
        state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED;

    const isCompletionButtonVisible =
        state === COMMUNICATION_TIMELINE_ENTRY.CLOSED &&
        (assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED ||
            assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED ||
            assemblyState === ASSEMBLY_STATE.PROTOCOL_PUBLISHED ||
            assemblyState === ASSEMBLY_STATE.CLOSED);

    const isDateOrAgendaPublished =
        (state === COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED && assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED) ||
        (state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED && assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED);

    const isAgendaPublishedAndProtocolfileExists =
        communicationStore.isProtocolExists &&
        ((state === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED &&
            assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED) ||
            (state === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED &&
                assemblyState === ASSEMBLY_STATE.PROTOCOL_PUBLISHED));

    const titleRow = (
        <React.Fragment>
            <div
                className={
                    isActive
                        ? styles.CommunicationTimelineEntrySelectedTitleRow
                        : styles.CommunicationTimelineEntryTitleRow
                }
            >
                <div className={styles.CommunicationTimelineEntryStateTitle}>
                    {t(`screens.assemblies.communicationcontent.states.${state}`)}
                </div>
            </div>
            <div
                className={
                    isActive
                        ? styles.CommunicationTimelineEntrySelectedTextRow
                        : styles.CommunicationTimelineEntryTextRow
                }
            >
                {t(`screens.assemblies.communicationcontent.${state}_text`)}
            </div>
        </React.Fragment>
    );

    const changeEntries = (
        <React.Fragment>
            {statusChangeEntry || publishChangeEntry ? (
                <div className={styles.CommunicationTimelineEntryStatusChangeRow}>
                    <p>
                        {statusChangeEntry && (
                            <span>
                                {statusChangeEntry?.date} - {statusChangeEntry?.label} {statusChangeEntry?.username}.
                            </span>
                        )}
                        {statusChangeEntry && publishChangeEntry ? <br /> : undefined}
                        {publishChangeEntry && (
                            <span>
                                {publishChangeEntry?.date} - {publishChangeEntry?.label} {publishChangeEntry?.username}.
                            </span>
                        )}
                    </p>
                </div>
            ) : undefined}
        </React.Fragment>
    );

    return (
        <div
            className={
                isActive
                    ? `${styles.CommunicationTimelineEntrySelectedContainer} ${assemblyStore.currentAssembly?.state}`
                    : styles.CommunicationTimelineEntryContainer
            }
        >
            <div className={`${styles.CommunicationTimelineEntryContent} `}>
                {titleRow}
                {changeEntries}
                {isNotificationButtonVisible && isDateOrAgendaPublished ? (
                    <div className={styles.ButtonRow}>
                        <CommunicationNotifyButtons entry={props.entry} />
                        <CommunicationPublishButtons entry={props.entry} />
                    </div>
                ) : null}
                {isNotificationButtonVisible && !isDateOrAgendaPublished ? (
                    <div className={styles.ButtonRow}>
                        <CommunicationPublishButtons entry={props.entry} />
                        <CommunicationNotifyButtons entry={props.entry} />
                    </div>
                ) : null}
                {assemblyState === ASSEMBLY_STATE.PROTOCOL_PUBLISHED &&
                props.entry.state === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED ? (
                    <div className={styles.ButtonRow}>
                        <CommunicationNotifyButtons entry={props.entry} />
                        <CommunicationPublishButtons
                            entry={props.entry}
                            isAgendaPublishedAndProtocolfileExists={isAgendaPublishedAndProtocolfileExists}
                        />
                    </div>
                ) : props.entry.state === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED ? (
                    <div className={styles.ButtonRow}>
                        <CommunicationPublishButtons
                            entry={props.entry}
                            isAgendaPublishedAndProtocolfileExists={isAgendaPublishedAndProtocolfileExists}
                        />
                        <CommunicationNotifyButtons entry={props.entry} />
                    </div>
                ) : undefined}
                {isCompletionButtonVisible && <CommunicationCompletionButton />}
            </div>
        </div>
    );
};

export const CommunicationTimelineEntry = Sentry.withProfiler(observer(CommunicationTimelineEntryBase));
